/* eslint-disable max-lines-per-function */
import React, { ChangeEvent, FC, FormEvent, useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Button, Col, Container, FormText, Row } from 'reactstrap';
import { ClearPiiTask, ConventionSharedModel } from '../../../../shared/convention';
import { ActionButtonModal, LinkButton, PermissionBoundary } from '../../../components';
import { TimeZoneDropdown } from '../../../components/TimeZoneDropdown';
import { useTranslation } from '../../../translations';
import { Fetcher, omit, useConvention, useFetcher } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import {
  ConfigBooleanSetting,
  ConfigDateSetting,
  ConfigNumberBoxSetting,
  ConfigTextBoxSetting,
} from './AbstractLiveSettings';

export const ConventionSettings: FC = () => {
  const convention = useConvention();

  const fetcher = useFetcher(async () => {
    return await api.getConvention(convention.id);
  });

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <ConventionSettingsEdit
      clearPiiTask={fetcher.data.clearPiiTask}
      initialConvention={fetcher.data.convention}
      refresh={fetcher.refresh}
    />
  );
};

const ConventionSettingsEdit: FC<{
  readonly initialConvention: ConventionSharedModel;
  readonly clearPiiTask?: ClearPiiTask;
  readonly refresh: () => void;
}> = ({ initialConvention, clearPiiTask, refresh }) => {
  const { ts } = useTranslation();
  const [convention, setConvention] = useState(initialConvention);
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    if (saving) {
      return;
    }

    setSaving(true);

    try {
      await api.updateConvention(initialConvention.id, omit(convention, 'id'));
      toast.success(ts('convention_settings_saved'));
    } catch (error) {
      captureError(error as Error);
    }

    setSaving(false);
  }, [saving, initialConvention.id, convention, ts]);

  const update = useCallback(
    <K extends keyof ConventionSharedModel>(key: K, value: ConventionSharedModel[K]) => {
      setConvention((old) => ({ ...old, [key]: value }));
    },
    [setConvention],
  );

  return (
    <div>
      {clearPiiTask && <ClearPiiWarning refresh={refresh} task={clearPiiTask} />}
      <h3>{ts('convention')}</h3>
      <hr />
      <Container className={saving ? 'savingConvention clearfix' : 'clearfix'} id="conventionForm">
        <ConfigTextBoxSetting
          description=""
          displayName="convention_legal_name"
          name="legalName"
          saving={saving}
          update={(val) => {
            update('legalName', val!);
          }}
          value={convention.legalName}
        />
        <ConfigTextBoxSetting
          description="convention_legal_type_description"
          displayName="convention_legal_type"
          name="legalType"
          placeholder="501c7_notforprofit"
          saving={saving}
          update={(val) => {
            update('legalType', val!);
          }}
          value={convention.legalType}
        />

        <ConfigTextBoxSetting
          description="maximum_6_characters"
          displayName="convention_short_name"
          name="shortName"
          saving={saving}
          update={(val) => {
            update('shortName', val!);
          }}
          value={convention.shortName}
        />

        <ConfigTextBoxSetting
          description="convention_notification_prefix_description"
          displayName="convention_notification_prefix"
          name="notificationPrefix"
          saving={saving}
          update={(val) => {
            update('notificationPrefix', val);
          }}
          value={convention.notificationPrefix}
        />

        <ConfigTextBoxSetting
          description="minimum_6_characters_shown_on"
          displayName="convention_long_type"
          name="longName"
          saving={saving}
          update={(val) => {
            update('longName', val!);
          }}
          value={convention.longName}
        />

        <ConfigTextBoxSetting
          description="usually_the_name_of_the"
          displayName="venue_name"
          name="venue"
          saving={saving}
          update={(val) => {
            update('venue', val!);
          }}
          value={convention.venue}
        />

        <TimeZoneDropdown
          onChange={(val) => {
            update('timeZone', val);
          }}
          saving={saving}
          value={convention.timeZone}
        />

        <ConfigDateSetting
          description="the_first_day_of_the"
          displayName="convention_start_date"
          name="startAt"
          saving={saving}
          timezone={convention.timeZone}
          update={(val) => {
            update('startAt', val);
          }}
          value={convention.startAt}
        />

        <ConfigDateSetting
          description="the_last_day_of_the"
          displayName="convention_end_date"
          name="endAt"
          saving={saving}
          timezone={convention.timeZone}
          update={(val) => {
            update('endAt', val);
          }}
          value={convention.endAt}
        />

        <ConfigNumberBoxSetting
          description="number_of_children_can_be"
          displayName="max_child_registration"
          name="maxChildren"
          saving={saving}
          update={(val) => {
            update('maxChildren', val!);
          }}
          value={convention.maxChildren}
        />

        <ConfigNumberBoxSetting
          description="max_subaccount_registration_description"
          displayName="max_subaccount_registration"
          name="maxSubAccounts"
          saving={saving}
          update={(val) => {
            update('maxSubAccounts', val!);
          }}
          value={convention.maxSubAccounts}
        />

        <ConfigNumberBoxSetting
          description="minimum_age_required_to_register"
          displayName="child_age_threshold"
          name="childAgeThreshold"
          saving={saving}
          update={(val) => {
            update('childAgeThreshold', val!);
          }}
          value={convention.childAgeThreshold}
        />

        <ConfigNumberBoxSetting
          description="minimum_age_required_before_requiring"
          displayName="minor_age_threshold"
          name="minorAgeThreshold"
          saving={saving}
          update={(val) => {
            update('minorAgeThreshold', val!);
          }}
          value={convention.minorAgeThreshold}
        />

        <ConfigBooleanSetting
          description="maintenance_mode_for_this_convention"
          displayName="convention_maintenance_mode"
          name="maintenanceMode"
          saving={saving}
          update={(val) => {
            update('maintenanceMode', val);
          }}
          value={convention.maintenanceMode}
        />

        <hr />
        <h3>{ts('event_store')}</h3>
        <ConfigBooleanSetting
          description="enables_disables_event_store"
          displayName="enable_event_store"
          name="enableEventStore"
          saving={saving}
          update={(val) => {
            update('enableEventStore', val);
          }}
          value={convention.enableEventStore}
        />

        <ConfigBooleanSetting
          description="hide_all_products_tab_from"
          disabled={!convention.enableEventStore}
          displayName="event_store_hide_all_products"
          name="hideEventStoreAllProducts"
          saving={saving}
          update={(val) => {
            update('hideEventStoreAllProducts', val);
          }}
          value={convention.hideEventStoreAllProducts}
        />

        <hr />
        <h3>{ts('users')}</h3>
        <ConfigBooleanSetting
          description="require_users_to_verify_their"
          displayName="require_email_confirmation_before_login"
          name="requireActivation"
          saving={saving}
          update={(val) => {
            update('requireActivation', val);
          }}
          value={convention.requireActivation}
        />

        <ConfigBooleanSetting
          description="show_elements_in_the_ui"
          displayName="show_returning_attendee_badge_on"
          name="showReturningAttendeePill"
          saving={saving}
          update={(val) => {
            update('showReturningAttendeePill', val);
          }}
          value={convention.showReturningAttendeePill}
        />

        <ConfigBooleanSetting
          description="__Allow attendees to edit the option values of their non-registration based orders."
          displayName="__Enable Attendees to Edit Order Options"
          name="enableAllOIOEdit"
          saving={saving}
          update={(val) => {
            update('enableAllOIOEdit', val);
          }}
          value={convention.enableAllOIOEdit}
        />

        <Button
          className="float-end"
          color="primary"
          disabled={saving}
          id="saveConvention"
          onClick={async () => {
            await save();
            refresh();
          }}
        >
          {ts('common.save')}
        </Button>
      </Container>
      <hr />
      <UploadLogoComponent />
      <PermissionBoundary requiredPermissions="kiosk:manage">
        <hr />
        <RegistrationFormEditor />
      </PermissionBoundary>
    </div>
  );
};

const ClearPiiWarning: FC<{
  readonly task: ClearPiiTask;
  readonly refresh: () => void;
}> = ({ task, refresh }) => {
  const { ts } = useTranslation();
  const convention = useConvention();

  const cancelDeletion = useCallback(async () => {
    try {
      await api.cancelNukePII(convention.id);
      toast.success(ts('convention.clearPii.cancel.success'));
    } catch (error) {
      captureError(error as Error);
    }

    refresh();
  }, [convention.id, refresh, ts]);

  return (
    <Row>
      <Col lg={12} xs={12}>
        <Alert className="clearfix" color="danger">
          <Row>
            <Col className="align-self-center">
              {ts('convention.clearPii.notice', task.runAt.toLocaleString())}
            </Col>
            <PermissionBoundary
              inline
              requiredPermissions={[
                'staff:admin:update',
                'pii:admin:read',
                'pii:admin:delete',
                'system:settings:update',
              ]}
            >
              <Col className="col-md-auto">
                <div className="float-end">
                  <ActionButtonModal
                    actionContent="convention.clearPii.cancel.confirmation"
                    buttonContent="convention.clearPii.cancel.label"
                    color="danger"
                    id="cancelPiiClearModal"
                    onComplete={async () => {
                      await cancelDeletion();
                    }}
                    outline
                    title="convention.clearPii.cancel.label"
                  >
                    {ts('convention.clearPii.cancel.prompt')}
                  </ActionButtonModal>
                </div>
              </Col>
            </PermissionBoundary>
          </Row>
        </Alert>
      </Col>
    </Row>
  );
};

const RegistrationFormEditor: FC = () => {
  const { ts } = useTranslation();
  const fetch = useFetcher(async () => {
    const pageLayout = await api.getRegistrationFormLayout(false);
    return { pageLayout };
  }, []);

  if (!fetch.success) {
    return <Fetcher result={fetch} />;
  }

  return (
    <div className="clearfix">
      <h3>{ts('form_editor')}</h3>
      <div className="start-align mt-10">
        <div>{ts('experimental_this_feature_is')}</div>
        <div className="float-end">
          <div>
            <LinkButton id="openRegEditor" to="/housekeeping/editor/registration">
              {ts('go_to')} {ts('registration_form_editor')}
            </LinkButton>
          </div>
        </div>
      </div>
    </div>
  );
};

const UploadLogoComponent: FC = () => {
  const { ts } = useTranslation();
  return (
    <div className="clearfix">
      <h3>{ts('logo')}</h3>
      <UploadLogoInnerComponent />
    </div>
  );
};

const UploadLogoInnerComponent: FC = () => {
  const { ts } = useTranslation();
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [success, setSuccess] = useState(false);
  const [artLoaded, setArtLoaded] = useState(false);

  const uploadInput = useRef<HTMLInputElement>(null);

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      if (!uploadInput.current) {
        return;
      }

      e.preventDefault();

      const file = uploadInput.current.files?.[0];
      if (!file) {
        toast.error(ts('no_file_was_selected'));
        return;
      }

      setUploadingLogo(true);

      try {
        await api.setLogo(file);
        setSuccess(true);
      } catch (error) {
        captureError(error as Error);
      } finally {
        setUploadingLogo(false);
      }
    },
    [ts],
  );

  const update = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget.files && e.currentTarget.files.length > 0) {
        setArtLoaded(true);
      } else if (!e.currentTarget.files || e.currentTarget.files.length === 0) {
        setArtLoaded(false);
      }
    },
    [setArtLoaded],
  );

  if (success) {
    return <div id="logoUploadSuccess">{ts('upload_success')}</div>;
  }

  if (uploadingLogo) {
    return <div id="logoUploadProgress">{ts('common.uploading')}</div>;
  }

  return (
    <div className="start-align mt-10">
      <form id="fileUpload" onSubmit={onSubmit}>
        <input
          accept="image/png"
          id="logoFile"
          name="logoFile"
          onChange={update}
          ref={uploadInput}
          type="file"
        />
        <FormText color="muted">{ts('logo_will_be_displayed_on')}</FormText>
        <Button className="float-end" color="primary" disabled={!artLoaded} id="submitLogo">
          {ts('save_logo')}
        </Button>
      </form>
    </div>
  );
};
